import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, imgSrc, formatApiErrorDetail } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { Wand2, Loader2, Save, PenLine, PackageOpen, Sparkles, Zap, Trash2, Upload } from "lucide-react";

const SCENES = [
  { id: "studio", label: "Studio minimaliste", prompt: "fond de studio minimaliste épuré, lumière douce et diffuse" },
  { id: "beton", label: "Béton brut", prompt: "décor de béton brut industriel, ambiance streetwear urbaine" },
  { id: "rue", label: "Rue urbaine", prompt: "rue urbaine, murs graffés, ambiance streetwear de ville" },
  { id: "neon", label: "Néons nocturnes", prompt: "ambiance nocturne avec néons colorés, reflets, mood nuit" },
  { id: "nature", label: "Nature / plein air", prompt: "décor nature en extérieur, lumière naturelle chaude" },
  { id: "plage", label: "Bord de mer", prompt: "ambiance bord de mer ensoleillée, lumière dorée d'été" },
  { id: "couleur", label: "Fond couleur", prompt: "fond de couleur unie et vibrante, style éditorial mode" },
  { id: "cosy", label: "Intérieur cosy", prompt: "intérieur cosy et chaleureux, matières textiles, lumière tamisée" },
];

const PLACEMENTS = [
  { id: "porte", label: "Porté (mannequin)", prompt: "le vêtement porté par un mannequin réaliste, pose lifestyle" },
  { id: "flatlay", label: "À plat (flat lay)", prompt: "le vêtement disposé à plat (flat lay) vu du dessus, composition soignée" },
  { id: "cintre", label: "Sur cintre", prompt: "le vêtement suspendu sur un cintre, présentation propre" },
  { id: "mouvement", label: "En mouvement", prompt: "le vêtement capté en plein mouvement dynamique" },
  { id: "gros-plan", label: "Gros plan", prompt: "gros plan sur le vêtement mettant en valeur la matière et le détail" },
  { id: "dos", label: "Vue arrière", prompt: "vue arrière/de dos du vêtement pour montrer le visuel dos" },
];

const ASPECTS = [
  { id: "square", label: "Carré 1:1", cls: "aspect-square" },
  { id: "portrait", label: "Portrait 4:5", cls: "aspect-[4/5]" },
  { id: "story", label: "Story 9:16", cls: "aspect-[9/16]" },
];
const aspectCls = (a) => ASPECTS.find((x) => x.id === a)?.cls || "aspect-square";

export default function StudioIA() {
  const { activeId, activeClient } = useClient();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [products, setProducts] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [scene, setScene] = useState("studio");
  const [placement, setPlacement] = useState("porte");
  const [aspect, setAspect] = useState("square");
  const [extra, setExtra] = useState("");
  const [generating, setGenerating] = useState(false);
  const [rafale, setRafale] = useState(false);
  const [variations, setVariations] = useState([]);
  const [savingId, setSavingId] = useState(null);

  const loadUploads = async (id) => {
    try {
      const { data } = await api.get("/media", { params: { client_id: id } });
      setUploads(data.filter((m) => m.mode === "upload" && m.media_type === "image"));
    } catch (err) {
      console.error("Failed to load imported photos:", err);
    }
  };

  useEffect(() => {
    if (!activeId) return;
    setVariations([]);
    setUploads([]);
    api.get("/products", { params: { client_id: activeId } }).then(({ data }) => {
      setProducts(data);
      setSelected((prev) =>
        prev && prev.kind === "product" && data.some((p) => p.id === prev.id)
          ? prev
          : data[0]
          ? { id: data[0].id, title: data[0].title, image_url: data[0].image_url, kind: "product" }
          : null
      );
    });
    loadUploads(activeId);
  }, [activeId]);

  const handleImport = async (files) => {
    if (!activeId) return toast.error("Sélectionne d'abord une marque");
    const list = Array.from(files || []).filter((f) => f.type.startsWith("image/"));
    if (!list.length) return toast.error("Importe une image (JPG, PNG…)");
    setUploading(true);
    let first = null;
    for (const file of list) {
      const form = new FormData();
      form.append("client_id", activeId);
      form.append("file", file);
      try {
        const { data } = await api.post("/media/upload", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (!first) first = data.media_id;
      } catch (e) {
        toast.error(`${file.name} : ${formatApiErrorDetail(e.response?.data?.detail)}`);
      }
    }
    setUploading(false);
    await loadUploads(activeId);
    if (first) {
      setSelected({ id: first, title: "Photo importée", image_url: `/api/media/${first}`, kind: "media" });
      toast.success(`${list.length} photo(s) importée(s)`);
    }
  };

  const composePrompt = (sceneId) => {
    const s = SCENES.find((x) => x.id === sceneId)?.prompt || "";
    const p = PLACEMENTS.find((x) => x.id === placement)?.prompt || "";
    return (
      `Garde EXACTEMENT le même vêtement que sur l'image de référence : mêmes couleurs, même imprimé/broderie, ` +
      `même forme et mêmes proportions — ne modifie pas le vêtement lui-même. ` +
      `Change uniquement le décor, l'ambiance, la lumière, le cadrage et la mise en scène. ` +
      `Mise en scène souhaitée : ${p}. Décor / ambiance : ${s}.` +
      (extra ? ` Détails supplémentaires : ${extra}.` : "")
    );
  };

  const genOne = async (sceneId) => {
    const { data } = await api.post("/generate-visual", {
      client_id: activeId,
      mode: "dress",
      product_id: selected.kind === "product" ? selected.id : undefined,
      media_id: selected.kind === "media" ? selected.id : undefined,
      aspect,
      prompt: composePrompt(sceneId),
    });
    return { ...data, product: selected, scene: sceneId, placement, aspect, key: data.media_id };
  };

  const generate = async () => {
    if (!activeId) return toast.error("Sélectionne une marque");
    if (!selected) return toast.error("Sélectionne un vêtement");
    setGenerating(true);
    try {
      const v = await genOne(scene);
      setVariations((prev) => [v, ...prev]);
      toast.success("Nouvelle variation générée");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    setGenerating(false);
  };

  const generateRafale = async () => {
    if (!activeId) return toast.error("Sélectionne une marque");
    if (!selected) return toast.error("Sélectionne un vêtement");
    setRafale(true);
    const idx = SCENES.findIndex((s) => s.id === scene);
    const chosen = [0, 1, 2, 3].map((k) => SCENES[(idx + k) % SCENES.length].id);
    try {
      const results = await Promise.allSettled(chosen.map((sid) => genOne(sid)));
      const ok = results.filter((r) => r.status === "fulfilled").map((r) => r.value);
      if (ok.length) setVariations((prev) => [...ok, ...prev]);
      toast[ok.length ? "success" : "error"](
        ok.length ? `${ok.length} décors générés` : "La génération en rafale a échoué"
      );
    } catch (e) {
      toast.error("Erreur pendant la rafale");
    }
    setRafale(false);
  };

  const saveDraft = async (v) => {
    setSavingId(v.key);
    try {
      await api.post("/posts", {
        client_id: activeId,
        image_url: v.image_url,
        product_title: v.product.title,
        caption: "",
        hashtags: [],
        status: "draft",
      });
      toast.success("Enregistré en brouillon");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    setSavingId(null);
  };

  const openComposer = (v) => {
    navigate("/composer", {
      state: { product: v.product, aiImage: { image_url: v.image_url, data_url: v.data_url } },
    });
  };

  const deleteVariation = async (v) => {
    if (!window.confirm("Supprimer définitivement ce visuel IA ? Cette action est irréversible.")) return;
    try {
      if (v.media_id) await api.delete(`/media/${v.media_id}`);
      setVariations((prev) => prev.filter((x) => x.key !== v.key));
      toast.success("Visuel supprimé définitivement");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <>
      <PageHeader overline={`Studio visuel · ${activeClient?.name || "—"}`} title="Studio IA">
        <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-muted-foreground hidden md:inline">
          Même vêtement · nouveau décor
        </span>
      </PageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-12">
        {/* controls */}
        <div className="lg:col-span-4 p-6 lg:p-8 lg:border-r border-white/10 space-y-6">
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">
                1 · Vêtement / photo
              </label>
              <button
                data-testid="studio-import-button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading || !activeId}
                className="inline-flex items-center gap-1.5 border border-accent/50 text-accent rounded-sm px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider hover:bg-accent/10 transition-colors disabled:opacity-50"
              >
                {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                Importer une photo
              </button>
            </div>
            <input
              ref={fileRef}
              data-testid="studio-file-input"
              type="file"
              accept="image/*,.heic,.heif"
              multiple
              className="hidden"
              onChange={(e) => handleImport(e.target.files)}
            />
            {products.length === 0 && uploads.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Importe une photo ci-dessus, ou importe d'abord la galerie de la marque.
              </p>
            ) : (
              <div className="flex gap-3 overflow-x-auto pb-2" data-testid="studio-product-picker">
                {uploads.map((m) => {
                  const src = { id: m.id, title: "Photo importée", image_url: m.image_url, kind: "media" };
                  const active = selected?.kind === "media" && selected?.id === m.id;
                  return (
                    <button
                      key={`up-${m.id}`}
                      onClick={() => setSelected(src)}
                      data-testid={`studio-pick-media-${m.id}`}
                      title="Photo importée"
                      className={`relative shrink-0 w-20 h-20 border rounded-sm bg-[#0d0d0d] overflow-hidden transition-all duration-150 ${
                        active ? "border-accent ring-1 ring-accent" : "border-white/10 hover:border-white/30"
                      }`}
                    >
                      <img src={imgSrc(m.image_url)} alt="import" className="w-full h-full object-cover" />
                      <span className="absolute bottom-0 inset-x-0 bg-accent/90 text-accent-foreground text-[8px] font-bold uppercase tracking-wider text-center py-0.5">
                        Importée
                      </span>
                    </button>
                  );
                })}
                {products.map((p) => {
                  const active = selected?.kind === "product" && selected?.id === p.id;
                  return (
                    <button
                      key={p.id}
                      onClick={() => setSelected({ id: p.id, title: p.title, image_url: p.image_url, kind: "product" })}
                      data-testid={`studio-pick-${p.id}`}
                      className={`shrink-0 w-20 h-20 border rounded-sm bg-[#0d0d0d] overflow-hidden transition-all duration-150 ${
                        active ? "border-accent ring-1 ring-accent" : "border-white/10 hover:border-white/30"
                      }`}
                    >
                      <img src={p.image_url} alt={p.title} className="w-full h-full object-contain p-1.5" />
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-3">
              2 · Décor / ambiance
            </label>
            <div className="grid grid-cols-2 gap-2">
              {SCENES.map((s) => (
                <button
                  key={s.id}
                  data-testid={`scene-${s.id}`}
                  onClick={() => setScene(s.id)}
                  className={`px-3 py-2 rounded-sm text-[11px] font-bold uppercase tracking-wider border text-left transition-all duration-150 ${
                    scene === s.id
                      ? "bg-primary border-primary text-primary-foreground"
                      : "border-white/15 text-muted-foreground hover:text-foreground hover:border-white/30"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-3">
              3 · Cadrage / position
            </label>
            <div className="grid grid-cols-2 gap-2">
              {PLACEMENTS.map((p) => (
                <button
                  key={p.id}
                  data-testid={`placement-${p.id}`}
                  onClick={() => setPlacement(p.id)}
                  className={`px-3 py-2 rounded-sm text-[11px] font-bold uppercase tracking-wider border text-left transition-all duration-150 ${
                    placement === p.id
                      ? "bg-accent border-accent text-accent-foreground"
                      : "border-white/15 text-muted-foreground hover:text-foreground hover:border-white/30"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-3">
              Format
            </label>
            <div className="flex gap-2">
              {ASPECTS.map((a) => (
                <button
                  key={a.id}
                  data-testid={`aspect-${a.id}`}
                  onClick={() => setAspect(a.id)}
                  className={`flex-1 px-2 py-2 rounded-sm text-[11px] font-bold uppercase tracking-wider border transition-all duration-150 ${
                    aspect === a.id
                      ? "bg-primary border-primary text-primary-foreground"
                      : "border-white/15 text-muted-foreground hover:text-foreground hover:border-white/30"
                  }`}
                >
                  {a.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-3">
              4 · Détails (optionnel)
            </label>
            <textarea
              data-testid="studio-extra-input"
              value={extra}
              onChange={(e) => setExtra(e.target.value)}
              rows={2}
              placeholder="Ex : lumière dorée du coucher de soleil, quelques plantes, ambiance rétro…"
              className="w-full bg-transparent border border-white/15 rounded-sm px-4 py-2.5 text-sm outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-colors resize-y"
            />
          </div>

          <button
            data-testid="studio-generate-button"
            onClick={generate}
            disabled={generating || rafale || !selected}
            className="w-full bg-primary text-primary-foreground font-bold uppercase tracking-wider text-sm py-3.5 rounded-sm flex items-center justify-center gap-2 hover:bg-primary/80 hover:-translate-y-[1px] transition-all duration-150 disabled:opacity-50"
          >
            {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
            {generating ? "Génération…" : "Générer une variation"}
          </button>
          <button
            data-testid="studio-rafale-button"
            onClick={generateRafale}
            disabled={generating || rafale || !selected}
            className="w-full border border-accent/50 text-accent font-bold uppercase tracking-wider text-sm py-3 rounded-sm flex items-center justify-center gap-2 hover:bg-accent/10 transition-all duration-150 disabled:opacity-50"
          >
            {rafale ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
            {rafale ? "Rafale en cours…" : "Rafale · 4 décors d'un coup"}
          </button>
          <p className="text-[10px] text-muted-foreground text-center">
            Le vêtement reste identique — seul le décor change. Génère autant de variations que tu veux.
          </p>
        </div>

        {/* results */}
        <div className="lg:col-span-8 p-6 lg:p-8">
          {variations.length === 0 && !generating && !rafale ? (
            <div className="border border-dashed border-white/15 rounded-sm p-12 text-center">
              <Sparkles className="h-8 w-8 text-muted-foreground mx-auto mb-4" />
              <p className="text-sm text-muted-foreground">
                Choisis un vêtement, un décor et un cadrage, puis génère une mise en scène.
                Le vêtement garde son visuel d'origine, on ne change que l'ambiance.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="studio-results">
              {(generating || rafale) && (
                <div className="border border-white/10 rounded-sm bg-secondary aspect-square flex flex-col items-center justify-center gap-3">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  <span className="text-[11px] text-muted-foreground uppercase tracking-wider">
                    {rafale ? "Rafale…" : "Création…"}
                  </span>
                </div>
              )}
              {variations.map((v, i) => (
                <div
                  key={v.key}
                  data-testid={`studio-variation-${i}`}
                  className="border border-white/10 rounded-sm bg-secondary overflow-hidden animate-fade-up"
                >
                  <img src={v.data_url} alt="variation" className={`w-full ${aspectCls(v.aspect)} object-cover`} />
                  <div className="p-2 flex items-center gap-2 border-t border-white/10">
                    <button
                      data-testid={`studio-compose-${i}`}
                      onClick={() => openComposer(v)}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 text-[10px] font-bold uppercase tracking-wider border border-white/15 rounded-sm hover:bg-white/[0.05] transition-colors"
                    >
                      <PenLine className="h-3.5 w-3.5" /> Composer
                    </button>
                    <button
                      data-testid={`studio-draft-${i}`}
                      onClick={() => saveDraft(v)}
                      disabled={savingId === v.key}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 text-[10px] font-bold uppercase tracking-wider bg-primary text-primary-foreground rounded-sm hover:bg-primary/80 transition-colors disabled:opacity-50"
                    >
                      {savingId === v.key ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                      Brouillon
                    </button>
                    <button
                      data-testid={`studio-delete-${i}`}
                      onClick={() => deleteVariation(v)}
                      title="Supprimer définitivement"
                      className="flex items-center justify-center py-2 px-2.5 text-[10px] font-bold uppercase tracking-wider border border-white/15 text-muted-foreground rounded-sm hover:text-accent hover:border-accent/40 transition-colors"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
